import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CircularProgress, TextField, makeStyles } from '@material-ui/core';
import ActionButton from '../../../actionButton';
import Scrollbars from '../../../scrollbars';
import ToggleComponent from '../../../toggleComponent';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import { LEVELING_AGENT_MESSAGE_TYPES } from '../../../../../constants/levelingAgent';

const TEXTAREA_MAX_HEIGHT = 'calc((100vh - 71px) / 2 - 96px)';
const TOOLBAR_HEIGHT = 38;

const useStyles = makeStyles(theme => ({
  composer: {
    width: '100%',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputFrame: {
    boxSizing: 'border-box',
    position: 'relative',
    display: 'grid',
    gridTemplateAreas: '"content" "toolbar"',
    gridTemplateRows: `auto ${TOOLBAR_HEIGHT}px`,
    maxHeight: TEXTAREA_MAX_HEIGHT,
    border: `1px solid ${theme.palette.primary.bluish6}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
    transition: theme.transitions.create(['border-color', 'background-color']),
    '&:focus-within': {
      borderColor: theme.palette.primary.blue1,
    },
  },
  inputFrameDisabled: {
    backgroundColor: theme.palette.primary.bluish9,
  },
  scrollArea: {
    gridArea: 'content',
    position: 'relative',
    minHeight: 0,
  },
  scrollViewport: {
    maxHeight: `calc(${TEXTAREA_MAX_HEIGHT} - ${TOOLBAR_HEIGHT}px - 2px)`,
  },
  scrollY: {
    right: -15,
  },
  input: {
    width: '100%',
    '& .MuiInput-root': {
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      minHeight: 'unset',
      transition: 'none',
      '&.Mui-focused': {
        border: 'none',
      },
      '&.Mui-disabled': {
        color: theme.palette.primary.bluish5,
        backgroundColor: 'transparent',
        border: 'none',
      },
    },
    '& .MuiInputBase-root': {
      boxSizing: 'border-box',
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent',
      minHeight: 'auto',
      padding: theme.spacing(2.125, 3.5),
      alignItems: 'flex-start',
      '&.Mui-focused': {
        border: 'none',
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        border: 'none',
        backgroundColor: 'transparent',
      },
    },
    '& .MuiInputBase-inputMultiline, & .MuiInput-input': {
      padding: 0,
      backgroundColor: 'transparent',
      '&.Mui-disabled': {
        color: theme.palette.primary.bluish5,
        backgroundColor: 'transparent',
        WebkitTextFillColor: theme.palette.primary.bluish5,
      },
    },
    '& textarea': {
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      '&:disabled': {
        backgroundColor: 'transparent',
        WebkitTextFillColor: theme.palette.primary.bluish5,
      },
    },
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 1,
  },
  loadingProgress: {
    color: theme.palette.primary.bluish5,
  },
  toolbar: {
    gridArea: 'toolbar',
    boxSizing: 'border-box',
    height: TOOLBAR_HEIGHT,
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2, 2),
    flexShrink: 0,
    zIndex: 2,
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    flexShrink: 0,
  },
  actionButton: {
    padding: theme.spacing(0.75),
    width: 30,
    height: 30,
  },
  actionIcon: {
    width: 14,
    height: 14,
  },
}));

const Composer = ({
  className = undefined,
  translations,
  isLoading,
  isDisabled = false,
  kadarApiUrl,
  kadarApiToken = undefined,
  onSubmit,
  onStop,
}) => {
  const classes = useStyles();
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);
  const viewportRef = useRef(null);

  const overlayProps = {
    events: {
      updated: osInstance => {
        viewportRef.current = osInstance.elements().viewport;
      },
    },
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    const textarea = textareaRef.current;

    if (!viewport || !textarea || document.activeElement !== textarea) {
      return;
    }

    viewport.scrollTop = viewport.scrollHeight;
  }, [input]);

  const onSubmitMessage = useCallback(() => {
    const text = input.trim();
    if (!text || isDisabled || isLoading) {
      return;
    }

    setInput('');
    onSubmit(
      {
        messages: [{ type: LEVELING_AGENT_MESSAGE_TYPES.HUMAN, content: text }],
      },
      {
        streamMode: ['messages-tuple', 'values'],
        config: {
          configurable: {
            kadar_api_url: kadarApiUrl,
            kadar_api_token: kadarApiToken,
          },
        },
      }
    );
  }, [input, isDisabled, isLoading, onSubmit, kadarApiUrl, kadarApiToken]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      onSubmitMessage();
    },
    [onSubmitMessage]
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key !== 'Enter' || event.shiftKey) {
        return;
      }

      event.preventDefault();
      onSubmitMessage();
    },
    [onSubmitMessage]
  );

  const handleStop = useCallback(() => {
    onStop();
  }, [onStop]);

  return (
    <form
      className={classNames(classes.composer, className)}
      onSubmit={handleSubmit}
    >
      <div className={classes.inputWrapper}>
        <div
          className={classNames(classes.inputFrame, {
            [classes.inputFrameDisabled]: isDisabled || isLoading,
          })}
        >
          <div className={classes.scrollArea}>
            <Scrollbars
              viewportClass={classes.scrollViewport}
              verticalScrollClass={classes.scrollY}
              overlayProps={overlayProps}
            >
              <TextField
                className={classes.input}
                inputRef={textareaRef}
                InputProps={{
                  disableUnderline: true,
                }}
                value={input}
                onChange={event => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={translations.placeholder}
                variant="standard"
                size="small"
                multiline
                minRows={5}
                disabled={isDisabled || isLoading}
                fullWidth
              />
            </Scrollbars>
          </div>
          <div className={classes.toolbar}>
            <div className={classes.actionButtons}>
              <ToggleComponent inKey={isLoading}>
                {isLoading ? (
                  <ActionButton
                    iconButtonClass={classes.actionButton}
                    iconClass={classes.actionIcon}
                    type={ACTION_BUTTON_TYPES.STOP}
                    tooltipText={translations.stopTooltip}
                    onClickHandler={handleStop}
                  />
                ) : (
                  <ActionButton
                    iconButtonClass={classes.actionButton}
                    iconClass={classes.actionIcon}
                    type={ACTION_BUTTON_TYPES.SEND}
                    isDisabled={isDisabled || !input.trim()}
                    tooltipText={translations.sendTooltip}
                    onClickHandler={onSubmitMessage}
                  />
                )}
              </ToggleComponent>
            </div>
          </div>
          {isLoading && (
            <div className={classes.loadingOverlay}>
              <CircularProgress
                classes={{ colorPrimary: classes.loadingProgress }}
                size={24}
              />
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

Composer.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  kadarApiUrl: PropTypes.string.isRequired,
  kadarApiToken: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onStop: PropTypes.func.isRequired,
};

export default Composer;
